/**
 * Phase 31 (plan 02) extracted reusable chat primitives into ../chat/.
 * This file is now a thin shim that:
 *  1. Re-exports MessageList from ../chat/MessageList with the technician
 *     miniapp's defaults wired in (ticket-id dividers visible).
 *  2. Re-exports MessageBubble for any consumer that imports it from here
 *     (none currently, but keep the surface stable).
 *
 * Configuration (authTokenKey, mediaProxyBase) is read by the chat surface from
 * ChatConfigContext. The technician miniapp does NOT wrap a ChatConfigProvider
 * because the default context values (authTokenKey='support_auth_token',
 * mediaProxyBase='/api/media') already match today's behavior. If main.tsx
 * later needs to wrap a provider (e.g. for a different localStorage key in
 * a new build), do it there — NOT in this shim.
 *
 * Do NOT add new logic to this file. New shared chat features go in ../chat/;
 * technician-only chrome (close modal, service-type modal, etc.) lives next
 * to TicketDetail.tsx.
 */

import { useMemo } from 'react';
import type { Message } from '../../types';
import type { ExternalContext } from '../../services/api';
import { useBridge } from '../../hooks/useBridge';
import {
  MessageList as SharedMessageList,
  MessageBubble as SharedMessageBubble,
} from '../chat';

interface TicketMessageListProps {
  messages: Message[];
  userFirstName: string;
  // Phase 32 inline-divider context — forwarded straight through to the
  // shared MessageList, where the showTicketDividers=true branch consumes them.
  ticketsExternal?: Record<string, ExternalContext>;
  onRefreshTicketExternal?: (ticketId: string) => void;
  refreshingTicketIds?: Set<string>;
  failedRefreshTicketIds?: Set<string>;
  // Multi-select close — when active, ticket dividers render checkboxes.
  // Only IDs in `selectableTicketIds` are tappable; the rest show a lock
  // hint. Forwarded straight through to SharedMessageList.
  selectionMode?: boolean;
  selectedTicketIds?: Set<string>;
  selectableTicketIds?: Set<string>;
  onToggleTicketSelection?: (ticketId: string) => void;
  /** Tap a default-mode divider to enter selection mode pre-selecting
   *  that ticket. Replaces the dedicated "Select" header button. */
  onTicketDividerTap?: (ticketId: string) => void;
  /** Phase 34: long-press a CLOSED-ticket divider to fire this handler
   *  (single-target reopen flow). Forwarded to SharedMessageList; absent →
   *  closed dividers render as today's plain chrome. */
  onTicketDividerLongPressForReopen?: (ticketId: string) => void;
  // v4.1 message-level multi-select & long-press. Forwarded through to
  // SharedMessageList. Optional — when omitted, the shared component
  // renders bubbles without the long-press wrapper.
  onMessageLongPress?: (
    message: Message,
    anchorRect: DOMRect,
    bubbleHtml: string,
    isMine: boolean
  ) => void;
  messageSelectionMode?: boolean;
  selectedMessageIds?: Set<number>;
  onToggleMessageSelection?: (messageId: number) => void;
  /** Phase 33: swipe-left-to-reply. Forwarded straight through to
   *  SharedMessageList; absent → no swipe gesture is wired. */
  onMessageSwipeReply?: (message: Message) => void;
  /** Phase 33: tap in-bubble quoted preview → jump to original. */
  onJumpToMessage?: (targetMessageId: number) => void;
  /** Forwarded to SharedMessageList — fires when the user expands a
   *  closed-history pill, used by TicketDetail to release its
   *  pagination gate. */
  onExpandClosedGroup?: () => void;
  /** Controls the always-compact closed-section collapse. Defaults to
   *  true (the technician miniapp's standard behavior). TicketDetail
   *  flips it false after the user explicitly opts into closed history
   *  by tapping the pill — so newly paginated batches render inline
   *  (with real height) instead of cascading into a re-collapsed pill. */
  compactClosedTickets?: boolean;
}

// Decode the JWT payload to extract the viewer's deviceId. Same pattern as
// Dashboard.tsx — read once per shim mount, never refresh. localStorage doesn't
// change during a component's lifetime in this app; the user logs out by
// reloading the page.
function readViewerDeviceId(): string {
  const token = localStorage.getItem('support_auth_token');
  if (!token) return '';
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload?.deviceId === 'string' ? payload.deviceId : '';
  } catch {
    return '';
  }
}

export function MessageList({
  messages,
  userFirstName,
  ticketsExternal,
  onRefreshTicketExternal,
  refreshingTicketIds,
  failedRefreshTicketIds,
  selectionMode,
  selectedTicketIds,
  selectableTicketIds,
  onToggleTicketSelection,
  onTicketDividerTap,
  onTicketDividerLongPressForReopen,
  onMessageLongPress,
  messageSelectionMode,
  selectedMessageIds,
  onToggleMessageSelection,
  onMessageSwipeReply,
  onJumpToMessage,
  onExpandClosedGroup,
  compactClosedTickets = true,
}: TicketMessageListProps) {
  // Positioning differs by mode, labels do not:
  //   - Iframe-embedded (?mode=embedded): identity-based — only THIS viewer's
  //     own technician messages go right; other technicians' messages go LEFT
  //     with their technicianName label.
  //   - Standalone Telegram miniapp: role-based — ALL technician messages go
  //     right, user messages go left (preserves the classic single-chatroom
  //     layout when multiple agents share one device-token / view).
  // In BOTH modes, right-side bubbles still render a sender label so an
  // agent can tell their own messages apart from other technicians'.
  const { isEmbedded } = useBridge();
  const viewerDeviceId = useMemo(
    () => (isEmbedded ? readViewerDeviceId() : ''),
    [isEmbedded]
  );
  return (
    <SharedMessageList
      messages={messages}
      userFirstName={userFirstName}
      showTicketDividers={true}
      viewerDeviceId={viewerDeviceId}
      showLabelOnMine={true}
      ticketsExternal={ticketsExternal}
      onRefreshTicketExternal={onRefreshTicketExternal}
      refreshingTicketIds={refreshingTicketIds}
      failedRefreshTicketIds={failedRefreshTicketIds}
      selectionMode={selectionMode}
      selectedTicketIds={selectedTicketIds}
      selectableTicketIds={selectableTicketIds}
      onToggleTicketSelection={onToggleTicketSelection}
      onTicketDividerTap={onTicketDividerTap}
      onTicketDividerLongPressForReopen={onTicketDividerLongPressForReopen}
      compactClosedTickets={compactClosedTickets}
      onMessageLongPress={onMessageLongPress}
      onMessageSwipeReply={onMessageSwipeReply}
      onJumpToMessage={onJumpToMessage}
      messageSelectionMode={messageSelectionMode}
      selectedMessageIds={selectedMessageIds}
      onToggleMessageSelection={onToggleMessageSelection}
      onExpandClosedGroup={onExpandClosedGroup}
    />
  );
}

export const MessageBubble = SharedMessageBubble;
